<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-vue-next';
import { parseISO } from 'date-fns';
import cloneDeep from 'lodash-es/cloneDeep';
import { VisuallyHidden } from 'radix-vue';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import { StudyPeriod } from '@/types';
import { store } from '@/plugins/store';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import TeamsDatePicker from './TeamsDatePicker.vue';
import Spinner from '@/components/ui/Spinner.vue';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import { i18n } from '@/plugins/internationalization/i18n';

interface Props {
    studyPeriods?: StudyPeriod[];
    disabled?: boolean;
    loading?: boolean;
    errorMessage?: string | null;
    displayValue?: string | null;
}

const props = defineProps<Props>();

const selectedStudyPeriods = defineModel<number[]>('selectedStudyPeriods', { required: true });
const selectedFromDate = defineModel<string>('selectedFromDate', { required: true });
const selectedToDate = defineModel<string>('selectedToDate', { required: true });

const farms = computed(() => store.state.farms);
const barns = computed(() => store.state.barns);

const options = computed(() => {
    if (!farms.value?.length || !barns.value?.length || !props.studyPeriods?.length) {
        return [];
    }

    const farmOptions: any[] = [];

    for (const farm of farms.value) {
        const barnOptions: any[] = [];

        const farmBarns = barns.value.filter((b) => b.farm_id === farm.farm_id);

        for (const barn of farmBarns) {
            const studyPeriods = props.studyPeriods
                .filter((p) => p.parent_location_id === barn.barn_id)
                .map((p) => ({
                    ...p,
                    checked: selectedStudyPeriods.value.includes(p.id),
                }));

            if (studyPeriods.length) {
                const checked = studyPeriods.every((p) => p.checked);
                const indeterminate = !checked && studyPeriods.some((p) => p.checked);

                barnOptions.push({
                    name: barn.name,
                    id: barn.barn_id,
                    studyPeriods,
                    checked,
                    indeterminate,
                });
            }
        }

        if (barnOptions.length) {
            const checked = barnOptions.every((b) => b.checked);
            const indeterminate = !checked && barnOptions.some((b) => b.checked);

            farmOptions.push({
                name: farm.name,
                id: farm.farm_id,
                barns: barnOptions,
                checked,
                indeterminate,
            });
        }
    }

    return farmOptions;
});

const isTeamsPickerOpen = ref(false);

const selectedActiveFarmIndex = ref<number | null>(null);
const selectedActiveBarnIndex = ref<number | null>(null);

const selectedActiveFarm = computed(() => (selectedActiveFarmIndex.value !== null ? options.value[selectedActiveFarmIndex.value] : null));
const selectedActiveBarn = computed(() =>
    selectedActiveFarmIndex.value !== null && selectedActiveBarnIndex.value !== null ? options.value[selectedActiveFarmIndex.value]?.barns[selectedActiveBarnIndex.value] : null
);

function selectActiveFarm(index: number) {
    selectedActiveFarmIndex.value = index;
    selectedActiveBarnIndex.value = null;
}

function selectActiveBarn(index: number) {
    selectedActiveBarnIndex.value = index;
}

function toggleFarm(farm: any) {
    if (farm.checked) {
        for (const barn of farm.barns) {
            toggleBarn({ ...barn, checked: true });
        }
    } else {
        for (const barn of farm.barns) {
            toggleBarn({ ...barn, checked: false });
        }
    }
}

function toggleBarn(barn: any) {
    const studyPeriodIds = barn.studyPeriods.map((p: any) => p.id);

    if (barn.checked) {
        selectedStudyPeriods.value = selectedStudyPeriods.value.filter((p) => !studyPeriodIds.includes(p));
    } else {
        selectedStudyPeriods.value = [...new Set([...selectedStudyPeriods.value, ...studyPeriodIds])];
    }
}

function toggleStudyPeriod(studyPeriod: any) {
    if (studyPeriod.checked) {
        selectedStudyPeriods.value = selectedStudyPeriods.value.filter((p) => p !== studyPeriod.id);
    } else {
        selectedStudyPeriods.value = [...new Set([...selectedStudyPeriods.value, studyPeriod.id])];
    }
}

const selectedStudyPeriodsToManage = computed<StudyPeriod[]>(() => props.studyPeriods?.filter((p) => selectedStudyPeriods.value.includes(p.id)) ?? []);

const showManageDialog = ref(false);

const studyPeriodsToManage = ref<StudyPeriod[]>([]);

async function openManageDialog() {
    studyPeriodsToManage.value = cloneDeep(selectedStudyPeriodsToManage.value);
    await nextTick();
    showManageDialog.value = true;
}

function removeManageStudyPeriod(optionId: number) {
    studyPeriodsToManage.value = studyPeriodsToManage.value.filter((o) => o.id !== optionId);
}

function saveManageStudyPeriods() {
    selectedStudyPeriods.value = studyPeriodsToManage.value.map((o) => o.id);
    showManageDialog.value = false;
}

const fromDatePickerConfig = computed(() => {
    if (!selectedToDate.value) {
        return {
            maxDate: new Date().toISOString(),
        };
    }

    return {
        maxDate: selectedToDate.value,
    };
});

const activeDisplayValue = computed(() => {
    if (props.displayValue) {
        return props.displayValue;
    }

    return i18n.global.t('views.monitoring.teamComparison.fields.teams.texts.noSelections');
});
</script>

<template>
    <Popover v-model:open="isTeamsPickerOpen">
        <PopoverTrigger as-child>
            <button
                class="mt-3 w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left disabled:opacity-60 disabled:pointer-events-none transition-colors"
                :class="{ 'border-red-500': errorMessage }"
                :disabled="disabled"
            >
                <div class="flex items-center gap-x-1 leading-normal">
                    <span class="font-medium">{{ $t('views.monitoring.teamComparison.fields.teams.label') }}:</span>
                    <span class="text-gray-500">{{ activeDisplayValue }}</span>
                </div>
                <ChevronDown class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': isTeamsPickerOpen }" aria-hidden="true" />
            </button>
        </PopoverTrigger>
        <PopoverContent trigger-width align="start" class="flex flex-col p-0 h-[516px]">
            <div class="flex justify-between gap-x-8 px-4 py-5 border-b">
                <div class="flex flex-col gap-y-1.5">
                    <div class="text-sm font-semibold">{{ $t('views.monitoring.teamComparison.fields.teamsPicker.texts.teamsWithStartDateInPeriod') }}</div>
                    <div class="flex items-center gap-x-2">
                        <TeamsDatePicker v-model="selectedFromDate" :config="fromDatePickerConfig" :prefix="$t('views.monitoring.teamComparison.fields.teamsPicker.fields.fromDate.prefix')" />
                        <TeamsDatePicker v-model="selectedToDate" :prefix="$t('views.monitoring.teamComparison.fields.teamsPicker.fields.toDate.prefix')" />
                    </div>
                </div>
                <div class="flex justify-end">
                    <Button variant="secondary" size="sm" :disabled="!selectedStudyPeriodsToManage.length || loading" @click="openManageDialog">
                        {{ $t('views.monitoring.teamComparison.fields.categoryPicker.buttons.manageSelected') }}
                        <div
                            v-if="selectedStudyPeriodsToManage.length > 0"
                            class="ml-1 text-sm flex-shrink-0 font-medium flex items-center justify-center p-1 rounded-full border border-primary-800 min-w-5 h-5"
                        >
                            {{ selectedStudyPeriodsToManage.length }}
                        </div>
                    </Button>
                </div>
            </div>
            <div role="tree" aria-orientation="horizontal" class="relative flex flex-grow divide-x max-h-full overflow-hidden">
                <div class="flex flex-col items-stretch w-[30%] flex-shrink-0" role="group">
                    <div class="sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2">{{ $t('views.monitoring.teamComparison.fields.teams.groups.farms') }}</div>
                    <div class="max-h-full overflow-y-auto">
                        <div
                            v-for="(farm, farmIndex) in options"
                            :key="farm.id"
                            class="flex items-center cursor-pointer"
                            role="treeitem"
                            :aria-label="farm.name"
                            aria-level="1"
                            :aria-expanded="selectedActiveFarmIndex === farmIndex"
                            :aria-setsize="options.length"
                            :aria-posinset="farmIndex + 1"
                            tabindex="0"
                            @keydown.enter="selectActiveFarm(farmIndex)"
                            @mouseover="selectActiveFarm(farmIndex)"
                            @click="selectActiveFarm(farmIndex)"
                        >
                            <label
                                class="flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer transition-colors"
                                :class="{
                                    'hover:bg-gray-100/70 focus:bg-gray-100/70': selectedActiveFarm?.barns?.length && selectedActiveFarmIndex !== farmIndex,
                                    'bg-primary-300 bg-opacity-30': selectedActiveFarmIndex === farmIndex,
                                }"
                            >
                                <CustomCheckbox :checked="farm.checked" :indeterminate="farm.indeterminate" disable-transition class="flex-shrink-0" @update:checked="toggleFarm(farm)" />
                                <div class="flex grow gap-x-3 items-center justify-between max-w-full min-w-0">
                                    <span class="grow select-none truncate">{{ farm.name }}</span>
                                    <ChevronRight v-if="farm.barns?.length" class="flex-shrink-0 h-4 w-4 -mr-1" />
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col items-stretch w-[30%] flex-shrink-0" role="group">
                    <div class="sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2">{{ $t('views.monitoring.teamComparison.fields.teams.groups.barns') }}</div>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-0"
                        leave-to-class="opacity-0"
                    >
                        <div v-if="selectedActiveFarm?.barns?.length" class="max-h-full overflow-y-auto">
                            <div
                                v-for="(barn, barnIndex) in selectedActiveFarm?.barns"
                                :key="barn.id"
                                role="treeitem"
                                :aria-label="barn.name"
                                aria-level="2"
                                :aria-expanded="selectedActiveBarnIndex === barnIndex"
                                :aria-setsize="selectedActiveFarm?.barns.length"
                                :aria-posinset="barnIndex + 1"
                                class="flex items-center cursor-pointer"
                                tabindex="0"
                                @keydown.enter="selectActiveBarn(barnIndex)"
                                @mouseover="selectActiveBarn(barnIndex)"
                                @click="selectActiveBarn(barnIndex)"
                            >
                                <label
                                    class="flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer transition-colors"
                                    :class="{
                                        'hover:bg-gray-100/70 focus:bg-gray-100/70': barn.studyPeriods?.length && selectedActiveBarnIndex !== barnIndex,
                                        'bg-primary-300 bg-opacity-30': selectedActiveBarnIndex === barnIndex,
                                    }"
                                >
                                    <CustomCheckbox :checked="barn.checked" :indeterminate="barn.indeterminate" disable-transition class="flex-shrink-0" @update:checked="toggleBarn(barn)" />
                                    <div class="flex grow gap-x-3 items-center justify-between max-w-full min-w-0">
                                        <span class="grow select-none truncate">{{ barn.name }}</span>
                                        <ChevronRight v-if="barn.studyPeriods?.length" class="flex-shrink-0 h-4 w-4 -mr-1" />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </transition>
                </div>
                <div class="flex flex-col items-stretch w-[40%] flex-shrink-0" role="group">
                    <div class="sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2">{{ $t('views.monitoring.teamComparison.fields.teams.groups.teams') }}</div>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-0"
                        leave-to-class="opacity-0"
                        mode="out-in"
                    >
                        <div v-if="selectedActiveBarn?.studyPeriods.length" class="max-h-full overflow-y-auto">
                            <div
                                v-for="studyPeriod in selectedActiveBarn.studyPeriods"
                                :key="studyPeriod.id"
                                role="treeitem"
                                :aria-label="studyPeriod.name"
                                aria-level="3"
                                class="flex items-center cursor-pointer"
                                tabindex="0"
                            >
                                <label class="flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer hover:bg-gray-100/70 focus:bg-gray-100/70 transition-colors">
                                    <CustomCheckbox :checked="studyPeriod.checked" disable-transition class="flex-shrink-0" @update:checked="toggleStudyPeriod(studyPeriod)" />
                                    <div class="grow flex items-center justify-between gap-x-2 select-none max-w-full min-w-0">
                                        <div class="flex items-center gap-x-1.5 min-w-0">
                                            <div class="whitespace-nowrap truncate min-w-0">{{ studyPeriod.name }}</div>
                                            <svg v-if="studyPeriod.is_favorite" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="shrink-0 size-4 text-yellow-400">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <div v-if="studyPeriod.start && studyPeriod.end" class="text-gray-500 text-sm whitespace-nowrap">
                                            {{ $d(parseISO(studyPeriod.start as string), 'short') }}{{ studyPeriod.end ? ` - ${$d(parseISO(studyPeriod.end as string), 'short')}` : '' }}
                                        </div>
                                        <div v-else-if="studyPeriod.start" class="text-gray-500 text-sm whitespace-nowrap">
                                            {{ $d(parseISO(studyPeriod.start as string), 'short') }}
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </transition>
                </div>
                <FadeTransition>
                    <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/90">
                        <div class="flex justify-center items-center gap-2">
                            <div class="text-lg font-medium">{{ $t('views.monitoring.teamComparison.fields.teamsPicker.texts.loading') }}</div>
                            <Spinner class="w-6 h-6 animate-spin" />
                        </div>
                    </div>
                </FadeTransition>
            </div>
            <div class="py-3 px-4 border-t">
                <ExpandCollapseTransition>
                    <div v-if="errorMessage" class="text-red-500 text-right font-medium">
                        <div class="pb-4">
                            {{ errorMessage }}
                        </div>
                    </div>
                </ExpandCollapseTransition>
                <div class="flex justify-end">
                    <Button class="w-44" :disabled="!!errorMessage" @click="isTeamsPickerOpen = false">{{ $t('views.monitoring.teamComparison.fields.teamsPicker.buttons.saveSelected') }}</Button>
                </div>
            </div>
            <Dialog v-model="showManageDialog" class="sm:max-w-[960px]">
                <DialogHeader>
                    <DialogTitle>
                        {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.title') }}
                    </DialogTitle>
                    <VisuallyHidden>
                        <DialogDescription>
                            {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.description') }}
                        </DialogDescription>
                    </VisuallyHidden>
                </DialogHeader>
                <div class="pt-2">
                    <table class="w-full">
                        <thead>
                            <tr>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.table.headers.name') }}
                                </th>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.table.headers.start') }}
                                </th>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.table.headers.end') }}
                                </th>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.table.headers.location') }}
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody class="border border-gray-100">
                            <tr v-for="studyPeriod in studyPeriodsToManage" :key="studyPeriod.id" class="odd:bg-gray-100">
                                <td class="pl-4 pr-2 py-2">
                                    {{ studyPeriod.name }}
                                </td>
                                <td class="pl-4 pr-2 py-2">
                                    {{ $d(parseISO(studyPeriod.start as string), 'short') }}
                                </td>
                                <td class="pl-4 pr-2 py-2">
                                    {{ $d(parseISO(studyPeriod.end as string), 'short') }}
                                </td>
                                <td class="pl-4 pr-2 py-2">
                                    {{ studyPeriod.location_name_augmented }}
                                </td>
                                <td class="text-right pl-4 pr-2 py-2">
                                    <Button variant="ghost" size="icon" class="text-red-500 hover:text-red-600 hover:bg-red-100" @click="removeManageStudyPeriod(studyPeriod.id)">
                                        <Trash2 class="w-4 h-4" />
                                    </Button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <DialogFooter class="flex sm:justify-center pt-2">
                    <DialogClose as-child>
                        <Button variant="ghost" size="lg" class="min-w-44">{{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.buttons.cancel') }}</Button>
                    </DialogClose>
                    <Button size="lg" class="min-w-44" @click="saveManageStudyPeriods">{{ $t('views.monitoring.teamComparison.fields.teamsPicker.dialogs.manageSelected.buttons.save') }}</Button>
                </DialogFooter>
            </Dialog>
        </PopoverContent>
    </Popover>
</template>
